import { JetSportBox } from "../../components/BoxView/JetSportBox";
import { InvertedPanel } from "../../components/BoxView/InvertedPanel";
import { OrderLineList } from "../order-details/subcomponents/OrderLineList";
import { SecondaryButton } from "../../components/Buttons/SecondaryButton";
import { PrimaryButton } from "../../components/Buttons/PrimaryButton";
import { PriceTotal } from "../order-details/subcomponents/PriceTotal";
import { BoxHeader2 } from "../../components/BoxView/BoxHeader2";
import { BoxContent } from "../../components/BoxView/BoxContent";
import { BoxFooter } from "../../components/BoxView/BoxFooter";
import { OrderForm } from "./subcomponents/OrderDeliveryForm";
import {
  DeliveryTypeOption,
  OrderDeliveryForm,
  PackagingOption,
} from "./useOrderConfirmationEditViewModel";
import {
  DeliveryType,
  Order,
  OrderLine,
  Packaging,
} from "../../../core/models/entities/Order";
import { LoadingView } from "../../components/loading-view/LoadingView";
import { useEffect } from "react";
import { useProfileContext } from "../../../contexts/ProfileContext";
import { Role } from "../../../core/models/entities/Role";
import { Switch } from "../../components/ui/Switch";

export interface OrderDeliveryFormViewModel {
  order: Order;
  orderNo: string;
  orderlines: OrderLine[];
  handleBack: () => void;
  handleChange: () => void;
  handleConfirm: () => void;
  form: OrderDeliveryForm;
  deliveryTypeGroupList: DeliveryTypeOption[];
  packagingGroupList: PackagingOption[];
  handleOrderFormUpdate: (form: OrderDeliveryForm) => void;
  handlePackagingOrDeliveryChange?: (
    deliveryType: DeliveryType,
    packaging: Packaging
  ) => void;
  subTotal: number;
  vat: number;
  totalAmount: number;
  isLoading: boolean;
  readonly?: boolean;
  isFormValid: boolean;
  validationErrors: string[];
  showValidation: boolean;
  showCustomerPrices: boolean;
  setShowCustomerPrices: (value: boolean) => void;
}

export function OrderConfirmationComponent({
  order,
  orderlines,
  orderNo,
  form,
  isLoading,
  handleOrderFormUpdate,
  handlePackagingOrDeliveryChange,
  handleBack,
  handleChange,
  handleConfirm,
  deliveryTypeGroupList,
  packagingGroupList,
  subTotal,
  vat,
  totalAmount,
  readonly,
  isFormValid,
  validationErrors,
  showValidation,
  showCustomerPrices,
  setShowCustomerPrices,
}: OrderDeliveryFormViewModel) {
  const { currency, role, organizationId } = useProfileContext();
  useEffect(() => {
    if (order.deliveryType == null || order.packagingType == null)
      handlePackagingOrDeliveryChange?.call(
        null,
        form.deliveryType,
        form.packaging
      );
  }, []);

  if (isLoading) return <LoadingView />;

  return (
    <JetSportBox className={"min-h-[900px]"}>
      <BoxHeader2 className={"text-sm flex justify-between items-center"}>
        <p className="uppercase">
          {readonly ? (
            <span>
              <b> Order</b> - #{orderNo}
            </span>
          ) : (
            <span className="font-bold">Confirm order</span>
          )}
        </p>
        {role === Role.Vendor &&
          readonly &&
          order.organization.id !== organizationId && (
            <div className={"flex items-center gap-x-2"}>
              <label className={"text-sm font-normal"}>
                Show customer prices
              </label>
              <Switch
                checked={showCustomerPrices}
                onCheckedChange={setShowCustomerPrices}
              />
            </div>
          )}
      </BoxHeader2>
      <BoxContent>
        <div className={"flex-1 flex-col my-10"}>
          <OrderLineList
            orderlines={orderlines}
            currency={order.currency}
            showCustomerPrices={showCustomerPrices}
          />
          <PriceTotal
            subTotal={subTotal}
            vat={vat}
            totalAmount={totalAmount}
            currency={order.currency ?? currency}
          />
        </div>
        <OrderForm
          form={form}
          order={order}
          readonly={readonly}
          onFormChange={handleOrderFormUpdate}
          onDeliveryOrPackagingChange={handlePackagingOrDeliveryChange}
          deliveryTypeOptionList={deliveryTypeGroupList}
          packagingOptionList={packagingGroupList}
          validationErrors={validationErrors}
          showValidation={showValidation}
        />
      </BoxContent>
      <div className={"flex justify-between ml-8 mr-10 mt-4"}>
        <SecondaryButton onClick={handleBack}>Back</SecondaryButton>
        <div className={"flex justify-between gap-x-8"}>
          {!readonly && (
            <>
              <SecondaryButton onClick={handleChange}>Change</SecondaryButton>
              <PrimaryButton
                onClick={handleConfirm}
                disabled={form.isFetchingDate}
              >
                Place Order
              </PrimaryButton>
            </>
          )}
        </div>
      </div>
      <BoxFooter className={"justify-between"}></BoxFooter>
    </JetSportBox>
  );
}
